import HttpStatusCode = ToroEnums.HttpStatusCode;

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import {
    HttpErrorResponse,
    HttpHandler,
    HttpHeaderResponse,
    HttpInterceptor,
    HttpProgressEvent,
    HttpRequest,
    HttpResponse,
    HttpSentEvent,
    HttpUserEvent
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AssetTrackingManagerService } from '../asset-tracking/asset-tracking-manager.service';
import { AuthManagerService } from './auth-manager.service';
import { CorrelationService } from '../../common/services/correlation.service';
import { DashAuthenticatedUser } from './models/dash-authenticated-user.model';
import { environment } from '../../../environments/environment';
import { LocalCacheService } from '../../common/services/local-cache.service';
import { LoggerService } from '../../common/services/logger.service';
import { ToroEnums } from '../../common/enumerations/toro.enums';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly ERROR_1 = 'Error_1';
    private readonly CORRELATION_ID_HEADER = 'X-Correlation-ID';

    isRefreshingToken = false;
    tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    isRefreshingAssetTrackingToken = false;
    assetTrackingTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    // It turns out that in an HttpInterceptor, you cannot inject anything that has a dependency on HttpClient into the constructor.
    // So to get around this, you simply need to inject the Injector Angular class into the HttpInterceptor constructor and then use
    // that in a JIT way within the injector.
    // userManager: UserManagerService;

    constructor(private correlationService: CorrelationService,
                private localCacheManager: LocalCacheService,
                private injector: Injector,
                private logger: LoggerService,
    ) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler):
        Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
        const accessToken = this.localCacheManager.dashApiAccessToken;
        const assetTrackingAccessToken = this.localCacheManager.assetTrackingApiAccessToken;

        let copiedRequest = this.shouldAddCorrelationId(req)
            ? this.addCorrelationId(req)
            : req;

        // let copiedRequest = req;

        if ((accessToken && accessToken !== '')
            && !req.url.includes('blob.core.windows.net')
            && !req.url.includes('api.greensightag.com')
            && !req.url.includes('connect.calamp.com')
            && !req.url.includes('api.pogoturfpro.com')
            && !req.url.includes(environment.assetTrackingApiUrl)
        ) {
            copiedRequest = copiedRequest.clone({
                headers: copiedRequest.headers
                    .set('Authorization', `bearer ${accessToken}`)
                    .set('Accept-Language', this.localCacheManager.language)
            });
        }

        if (req.url.includes(environment.assetTrackingApiUrl) && assetTrackingAccessToken && assetTrackingAccessToken !== '' && assetTrackingAccessToken !== 'null') {
            copiedRequest = copiedRequest.clone({
                headers: copiedRequest.headers
                    .set('Authorization', `bearer ${assetTrackingAccessToken}`)
            });
        }

        // TODO: TEMPORARY? GreenSight
        if (req.url.includes('api.greensightag.com')) {
            const gsaAccessToken = this.localCacheManager.gsaApiAccessToken;
            copiedRequest = copiedRequest.clone({
                headers: copiedRequest.headers
                    .set('Authorization', `bearer ${gsaAccessToken}`)
            });
        }

        return next.handle(copiedRequest)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    try {
                        if (!this.isExpectedBootstrapAuthError(copiedRequest, error)) {
                            this.logger.error(error['message'], error);
                        }
                    } catch (e) {
                        this.logger.error('Error in AuthInterceptor', e);
                    }

                    switch ((<HttpErrorResponse>error).status) {
                        case HttpStatusCode.BadRequest:
                            if (this.isExpectedBootstrapAuthError(copiedRequest, error)) {
                                return new Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>>();
                            }
                            return this.handle400Error(error);
                        case HttpStatusCode.Unauthorized:
                            return this.handle401Error(copiedRequest, next);

                        case HttpStatusCode.Forbidden:
                        case HttpStatusCode.FailedDependency:
                        default:
                            return throwError(() => error);
                    }
                })
            );

        // return next.handle(req);
    }

    handle401Error(req: HttpRequest<any>, next: HttpHandler) {
        // Handle Asset Tracking API missing or expired token
        if (req.url.includes(environment.assetTrackingApiUrl)) {
            return this.handleAssetTracking401Error(req, next);
        }

        // Handle standard IntelliDash API missing or expired token.
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.tokenSubject.next(null);

            const authManager = this.injector.get(AuthManagerService);

            return authManager.dashApiLogInWithRefreshToken()
                .pipe(
                    switchMap((user: DashAuthenticatedUser) => {
                        this.tokenSubject.next(user.token);
                        const copiedRequest = req.clone({ headers: req.headers.set('Authorization', `bearer ${user.token}`) });

                        this.isRefreshingToken = false;
                        return next.handle(copiedRequest);
                    }),
                    catchError((error: any) => {
                        if (error instanceof HttpErrorResponse && (<HttpErrorResponse>error).status === 400) {
                            // If there is an exception calling 'refreshToken', bad news so logout.
                            return this.handle400Error(error);
                        }

                        return throwError(error);
                    })
                );
        } else {
            return this.tokenSubject
                .pipe(
                    filter(token => token != null),
                    take(1),
                    switchMap(token => {
                        const copiedRequest = req.clone({ params: req.params.set('Authentication', token) });
                        return next.handle(copiedRequest);
                    })
                );
        }
    }

    handle400Error(error) {
        if (error && error.status === 400 && this.isRefreshTokenInvalid(error)) {
            // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
            const authManager = this.injector.get(AuthManagerService);
            authManager.dashApiLogOut();
            return throwError(error);
        }

        return throwError(error);
    }

    private shouldAddCorrelationId(req: HttpRequest<any>): boolean {
        return !req.url.includes('blob.core.windows.net')
            && !req.url.includes('api.greensightag.com')
            && !req.url.includes('connect.calamp.com')
            && !req.url.includes('api.pogoturfpro.com')
            && !req.url.includes(environment.assetTrackingApiUrl);
    }

    private isRefreshTokenInvalid(error: HttpErrorResponse) {
        // State when the refreshToken exists, but is expired. This will be thrown by the server.
        if (error.error && error.error[this.ERROR_1] && error.error[this.ERROR_1][0] && error.error[this.ERROR_1][0].includes('token is expired')) {
            return true;
        }

        // State when the refreshToken does not exist. This will be thrown by userManager.loginWithRefreshToken()
        if (error.statusText === 'no refresh token found') {
            return true;
        }

        return false;
    }

    private handleAssetTracking401Error(req: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshingAssetTrackingToken) {
            this.isRefreshingAssetTrackingToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.assetTrackingTokenSubject.next(null);

            const assetTrackingManager = this.injector.get(AssetTrackingManagerService);

            let tokenObservable: Observable<any>;
            if (this.localCacheManager.assetTrackingApiRefreshToken
                && this.localCacheManager.assetTrackingApiRefreshToken !== ''
                && this.localCacheManager.assetTrackingApiRefreshToken !== 'null'
            ) {
                tokenObservable = assetTrackingManager.apiLoginWithRefreshToken();
            } else {
                tokenObservable = assetTrackingManager.apiLogin();
            }

            return tokenObservable
                .pipe(
                    switchMap((user: DashAuthenticatedUser) => {
                        this.assetTrackingTokenSubject.next(user.token);
                        const copiedRequest = req.clone({ headers: req.headers.set('Authorization', `bearer ${user.token}`) });

                        this.isRefreshingAssetTrackingToken = false;
                        return next.handle(copiedRequest);
                    }),
                    catchError((error: any) => {
                        if (error instanceof HttpErrorResponse && (<HttpErrorResponse>error).status === 400) {
                            // If there is an exception calling 'refreshToken', bad news so logout.
                            return this.handle400Error(error);
                        }

                        return throwError(error);
                    })
                );
        } else {
            return this.assetTrackingTokenSubject
                .pipe(
                    filter(token => token != null),
                    take(1),
                    switchMap(token => {
                        const copiedRequest = req.clone({ params: req.params.set('Authentication', token) });
                        return next.handle(copiedRequest);
                    })
                );
        }
    }

    private addCorrelationId(req: HttpRequest<any>): HttpRequest<any> {
        if (req.headers.has(this.CORRELATION_ID_HEADER)) {
            return req;
        }

        const correlationId = this.correlationService.createCorrelationId();

        return req.clone({
            headers: req.headers.set(this.CORRELATION_ID_HEADER, correlationId)
        });
    }

    private isExpectedBootstrapAuthError(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
        return (req.url.includes('/api/user') && error.status === HttpStatusCode.Unauthorized)
            || (req.url.includes('/api/auth/token') && error.status === HttpStatusCode.BadRequest);
    }

}
